import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title, subtitle }) => {
  // Initialize sidebar state from localStorage
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar-collapsed');
      return saved === 'true';
    } catch (error) {
      console.error('Error reading sidebar state from localStorage:', error);
      return false;
    }
  });
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);
      // Auto-collapse sidebar on mobile (but don't save this to localStorage)
      if (isMobileView) {
        setIsSidebarCollapsed(true);
      } else {
        // On desktop, restore from localStorage
        try {
          const saved = localStorage.getItem('sidebar-collapsed');
          if (saved !== null) {
            setIsSidebarCollapsed(saved === 'true');
          }
        } catch (error) {
          console.error('Error reading sidebar state from localStorage:', error);
        }
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    
    // Save to localStorage (only on desktop to avoid conflicts with mobile auto-collapse)
    if (!isMobile) {
      try {
        localStorage.setItem('sidebar-collapsed', String(newState));
      } catch (error) {
        console.error('Error saving sidebar state to localStorage:', error);
      }
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <div className={`${isMobile && !isSidebarCollapsed ? 'fixed inset-0 z-50' : ''}`}>
        {/* Mobile overlay */}
        {isMobile && !isSidebarCollapsed && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={toggleSidebar}
          />
        )}
        
        <div className={`${isMobile ? 'fixed left-0 top-0 z-50' : 'relative'}`}>
          <Sidebar 
            isCollapsed={isSidebarCollapsed} 
            onToggle={toggleSidebar}
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
